import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  inject,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';

import { QueryStringTypesEnum } from 'src/app/configs/query-string-types';
import { GroupPriceSocialMediaDetailsService } from 'src/app/core/api/services';
import { EventTypes } from 'src/app/core/constants/event-types';
import { BaseIndexModule } from 'src/app/pages/shared/base/modules/base-index.module';
import {
  CustomResultOfListOfGroupPriceDetailDto,
  CustomResultOfListOfGroupPriceSocialMediaByGroupPriceIdDto,
  CustomResultOfPaginatedListOfGroupPriceByCategoryIdWithPaginationDto,
  GroupPriceByCategoryIdWithPaginationDto,
  GroupPriceDetailDto,
  GroupPriceSocialMediaByGroupPriceIdDto,
  PaginatedListOfGroupPriceByCategoryIdWithPaginationDto,
} from '../../../../core/api/models';
import { GroupPriceDto } from '../../../../core/api/models/group-price-dto';
import { GroupPriceDetailsService } from '../../../../core/api/services/group-price-details.service';
import { GroupPricesService } from '../../../../core/api/services/group-prices.service';
import { FormPageConfig } from '../../../../core/models/form-page-config.model';
import { DialogService } from '../../../../core/services/dialog.service';
import { BaseIndexPageComponent } from '../../../shared/base/components/base-index-page.component';
import { GroupPriceDetailsFormComponent } from '../group-price-details-form/group-price-details-form.component';
import { GroupPriceFormComponent } from '../group-price-form/group-price-form.component';
import { GroupPriceSocialMediaDetailFormComponent } from '../group-price-social-media-detail-form/group-price-social-media-detail-form.component';

@Component({
  selector: 'app-group-price',
  templateUrl: './group-price.component.html',
  imports: [
    BaseIndexModule,
    GroupPriceSocialMediaDetailFormComponent,
    GroupPriceDetailsFormComponent,
    GroupPriceFormComponent,
  ],
})
export class GroupPriceComponent
  extends BaseIndexPageComponent
  implements OnInit
{
  //#region Properties

  #groupPriceSocialMedia = inject(GroupPriceSocialMediaDetailsService);

  pageName = 'groupPrice';
  modelWithPagination$: Observable<PaginatedListOfGroupPriceByCategoryIdWithPaginationDto>;
  items$: Observable<Array<GroupPriceByCategoryIdWithPaginationDto>>;
  formPageConfig: FormPageConfig<GroupPriceDto> = {};

  //detail form
  isVisibleDetailForm: boolean = false;
  isVisibleSocialMediaDetailForm: boolean = false;

  formDetailPageConfig: FormPageConfig<GroupPriceDetailDto[]> = {};
  formSocialMediaDetailPageConfig: FormPageConfig<
    GroupPriceSocialMediaByGroupPriceIdDto[]
  > = {};

  @Input() categoryId: number;
  @Input() categoryName: string;
  @Output('onClose') output_onClose = new EventEmitter<boolean>();

  //#endregion

  constructor(
    protected currentService: GroupPricesService,
    public dialogService: DialogService,
    public groupPriceDetail: GroupPriceDetailsService,
    protected translate: TranslateService
  ) {
    super();
  }

  ngOnInit() {
    this.setPageService(this.currentService, 'groupPricesCreateGet');
    super.ngOnInit();
    this.pageConfig.showAuditBtn = false;
    this.pageConfig.showRefreshBtn = true;
    this.pageConfig.title = this.translate.instant(
      this.pageVocabs + 'titleWithCategoryName',
      { categoryName: this.categoryName }
    );
  }

  configTableColumns() {
    this.tableColumns = [
      { title: this.localizer.title, name: 'title', hasSearch: true },
      {
        title: this.localizer.minPrice,
        name: 'minPrice',
        needCurrency: true,
        hasSearch: true,
        type: 'number',
      },
      {
        title: this.localizer.maxPrice,
        name: 'maxPrice',
        needCurrency: true,
        hasSearch: true,
        type: 'number',
      },
      {
        title: `${this.tableVocabs}mtnSmartPercent`,
        name: 'mtnSmartPercent',
        hasSearch: true,
        type: 'number',
      },
      {
        title: `${this.tableVocabs}mciSmartPercent`,
        name: 'mciSmartPercent',
        hasSearch: true,
        type: 'number',
      },
      {
        title: `${this.tableVocabs}rightelSmartPercent`,
        name: 'rightelSmartPercent',
        hasSearch: true,
        type: 'number',
      },
      {
        title: this.localizer.description,
        name: 'description',
        noSorting: true,
      },
      {
        title: this.localizer.createDate,
        name: 'created',
        hasSearch: true,
        type: 'dateTime',
      },
      { title: this.localizer.default, name: 'isDefault' },
    ];
  }

  //#region Api

  //Get All
  getPaginationApi(): Observable<CustomResultOfPaginatedListOfGroupPriceByCategoryIdWithPaginationDto> {
    return this.currentService.groupPricesGetGroupPriceByCategoryId({
      body: {
        ...this.getBaseModelWithPaginationParams(),
        categoryId: this.categoryId,
      },
    });
  }

  //Get ById
  baseGetByIdApi(id: any): void {
    this.unSubs();

    this.pageConfig.loading = true;

    this.formPageConfig.editId = id;

    this.currentService
      .groupPricesUpdateGet({ Id: id })
      .pipe(this.basePipeOperators())
      .subscribe(this.baseGetByIdObserver);
  }

  //Delete
  baseDeleteApi(id: any) {
    this.unSubs();

    this.pageConfig.loading = true;
    this.lastSelectedItemId = id;

    this.currentService
      .groupPricesDelete({ id: id })
      .pipe(this.basePipeOperators())
      .subscribe(this.baseDeleteObserver);
  }

  getDetailsById(groupPriceId: number) {
    this.loading = true;
    this.pageConfig.loading = true;

    this.groupPriceDetail
      .groupPriceDetailsUpdateGet({ GroupPriceId: groupPriceId })
      .pipe(this.basePipeOperators())
      .subscribe((items: CustomResultOfListOfGroupPriceDetailDto) => {
        this.isVisibleDetailForm = true;
        this.formDetailPageConfig.editId = groupPriceId;
        if (items.data?.length) {
          this.formDetailPageConfig.isEditMode = true;
          this.formDetailPageConfig.title =
            this.pageVocabs + 'detailForm.editTitle';
          this.formDetailPageConfig.editData = items.data;
        } else {
          this.formDetailPageConfig.isEditMode = false;
          this.formSocialMediaDetailPageConfig.editData = null;
          this.formDetailPageConfig.title =
            this.pageVocabs + 'detailForm.addTitle';
        }
      });
  }

  getMediaDetailById(groupPriceId: number) {
    this.loading = true;
    this.pageConfig.loading = true;

    this.#groupPriceSocialMedia
      .groupPriceSocialMediaDetailsGetGroupPriceSocialMediaByGroupPriceId({
        GroupPriceId: groupPriceId,
      })
      .pipe(this.basePipeOperators())
      .subscribe(
        (items: CustomResultOfListOfGroupPriceSocialMediaByGroupPriceIdDto) => {
          this.isVisibleSocialMediaDetailForm = true;
          this.formSocialMediaDetailPageConfig.editId = groupPriceId;

          if (items.data?.length) {
            this.formSocialMediaDetailPageConfig.isEditMode = true;
            this.formSocialMediaDetailPageConfig.title =
              this.pageVocabs + 'socialMediaDetailForm.editTitle';
            this.formSocialMediaDetailPageConfig.editData = items.data;
          } else {
            this.formSocialMediaDetailPageConfig.isEditMode = false;
            this.formSocialMediaDetailPageConfig.editData = null;
            this.formSocialMediaDetailPageConfig.title =
              this.pageVocabs + 'socialMediaDetailForm.addTitle';
          }
        }
      );
  }

  //#endregion
  onCreate() {
    try {
      this.currentService[this.createGetApiMethodName]()
        .pipe(this.basePipeOperators())
        .subscribe(
          (res) => {
            if (res) {
              let params = this.routingState.parseQueryString();
              for (const [key, value] of Object.entries(params)) {
                if (key == QueryStringTypesEnum.Clone_key) {
                  this.setParentQueryString();

                  this.location.go(
                    window.location.pathname +
                      '?' +
                      QueryStringTypesEnum.Clone_key +
                      '=' +
                      value
                  );
                  this.pageConfig.isVisibleForm = true;
                }
              }
            }
          },
          (error) => {
            this.pageConfig.isVisibleForm = undefined;
            const timeout = setTimeout(() => {
              this.pageConfig.isVisibleForm = false;
              clearTimeout(timeout);
            }, 10);
          }
        );
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.systemManagement
      );
    }
  }

  closeDetails() {
    this.isVisibleDetailForm = false;
    this.isVisibleSocialMediaDetailForm = false;
    this.formDetailPageConfig.isEditMode = false;
  }

  //#endregion

  onClose() {
    this.output_onClose.emit(true);
  }
}
